import { CalendarOff, CalendarPlus, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { FormError } from '@/components/ui/field';
import {
  useDeleteOverride,
  useOverrides,
  useResolvedAvailability,
  useSaveOverride,
} from '@/hooks/scheduling';
import { ApiError } from '@/lib/api';
import { addMonths, monthGrid, sameMonth } from '@/lib/calendar';
import { formatDay, formatDuration, formatWallTime } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import { cn } from '@/lib/utils';
import { todayInBusinessZone } from '@shared/dates';
import type { DayAvailability, ScheduleOverrideView } from '@shared/schemas/availability';

/*
 * Date Overrides — the second tab of /admin/scheduling, ROADMAP.md §7.2.
 *
 * **An override wins outright over the weekly pattern**, in both directions:
 * "Nina is off on the 14th" and "Nina is covering Saturday" are the same kind of
 * row. That is why the calendar below paints from the *resolved* answer rather
 * than from the override list — the grid shows what the assignment screen will
 * think, which is the only version worth looking at.
 *
 * The month grid is the bounded read (P8): it asks for its 42 days and nothing
 * else, using the same pure `YYYY-MM-DD` arithmetic as the admin calendar. No
 * `new Date()` day maths anywhere here, so no daylight-saving hour can appear in
 * the middle of a day count.
 */

interface DraftOverride {
  date: string;
  isAvailable: boolean;
  startTime: string;
  endTime: string;
  breakMinutes: number;
  reason: string;
}

function blankDraft(date: string): DraftOverride {
  return {
    date,
    // Unavailable first, because that is what an override is for nine times out
    // of ten: somebody is off. Covering a day is the exception.
    isAvailable: false,
    startTime: '09:00',
    endTime: '17:00',
    breakMinutes: 0,
    reason: '',
  };
}

export function DateOverridesTab({
  employeeId,
  employeeName,
}: {
  employeeId: string;
  employeeName: string;
}) {
  const toast = useToast();
  const today = todayInBusinessZone();

  const [anchor, setAnchor] = useState(today);
  const grid = monthGrid(anchor);

  const resolved = useResolvedAvailability(employeeId, grid.from, grid.to);
  const overrides = useOverrides(employeeId, grid.from, grid.to);
  const save = useSaveOverride(employeeId);
  const remove = useDeleteOverride(employeeId);

  const [draft, setDraft] = useState<DraftOverride | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<ScheduleOverrideView | null>(null);

  const byDate = new Map<string, DayAvailability>(
    (resolved.data?.data ?? []).map((day) => [day.date, day]),
  );
  const overrideByDate = new Map<string, ScheduleOverrideView>(
    (overrides.data?.data ?? []).map((entry) => [entry.date, entry]),
  );

  const monthLabel = new Date(`${anchor.slice(0, 8)}01T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  function openFor(date: string): void {
    const existing = overrideByDate.get(date);
    setFormError(null);
    setDraft(
      existing === undefined
        ? blankDraft(date)
        : {
            date,
            isAvailable: existing.isAvailable,
            startTime: existing.startTime?.slice(0, 5) ?? '09:00',
            endTime: existing.endTime?.slice(0, 5) ?? '17:00',
            breakMinutes: existing.breakMinutes,
            reason: existing.reason ?? '',
          },
    );
  }

  function onSave(): void {
    if (draft === null) return;
    setFormError(null);

    save.mutate(
      {
        date: draft.date,
        isAvailable: draft.isAvailable,
        startTime: draft.isAvailable ? draft.startTime : null,
        endTime: draft.isAvailable ? draft.endTime : null,
        breakMinutes: draft.isAvailable ? draft.breakMinutes : 0,
        reason: draft.reason,
      },
      {
        onSuccess: () => {
          toast.success(`${formatDay(draft.date)} saved for ${employeeName}.`);
          setDraft(null);
        },
        onError: (error) => {
          setFormError(
            error instanceof ApiError ? error.message : 'That override could not be saved.',
          );
        },
      },
    );
  }

  if (resolved.isError || overrides.isError) {
    return (
      <ErrorState
        title="Those overrides could not be loaded"
        onRetry={() => {
          void resolved.refetch();
          void overrides.refetch();
        }}
      />
    );
  }

  return (
    /*
     * **`p-2` below `sm`, and that is a 44px rule rather than a taste.** A
     * seven-column month grid inside `p-4` at 375px gives cells of 43.86px — the
     * 375 sweep caught it, exactly as it caught a 43px filter pill in phase 6.
     * Nothing here can be narrower without the grid ceasing to be a month, so
     * the padding is what gives way.
     */
    <div className="p-2 sm:p-4">
      <div className="mb-3 flex items-center gap-2">
        <Button
          variant="outline"
          size="icon"
          aria-label="Previous month"
          onClick={() => {
            setAnchor(addMonths(anchor, -1));
          }}
        >
          <ChevronLeft aria-hidden="true" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          aria-label="Next month"
          onClick={() => {
            setAnchor(addMonths(anchor, 1));
          }}
        >
          <ChevronRight aria-hidden="true" />
        </Button>
        <h3 aria-live="polite" className="ml-1 text-h3 text-ink-900">
          {monthLabel}
        </h3>
        {anchor !== today ? (
          <Button
            variant="ghost"
            onClick={() => {
              setAnchor(today);
            }}
          >
            This month
          </Button>
        ) : null}
      </div>

      <Legend />

      {resolved.isPending ? (
        <div aria-busy="true">
          <span className="sr-only">Loading availability</span>
          <div aria-hidden="true" className="bg-paper-100 h-72 animate-pulse rounded-lg" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-ink-300 bg-white">
          <div className="grid grid-cols-7 border-b border-ink-100">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label) => (
              <div key={label} className="px-1 py-2 text-center text-meta font-medium text-ink-500">
                {label}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {grid.days.map((day) => {
              const answer = byDate.get(day);
              const override = overrideByDate.get(day);
              const inMonth = sameMonth(day, anchor);

              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => {
                    openFor(day);
                  }}
                  // The whole cell is the control, which is what makes 44×44
                  // trivially true here — and the label carries the resolved
                  // sentence, so a screen reader gets the same answer the colour
                  // is conveying. Colour alone is never the message.
                  //
                  // No full stop after `explanation`: the resolver returns whole
                  // sentences, already terminated, and adding one read as
                  // "on Fridays.." to anybody listening.
                  aria-label={`${formatDay(day)} — ${answer?.explanation ?? 'Not loaded.'} Set an override.`}
                  className={cn(
                    'min-h-[68px] border-b border-r border-ink-100 p-1.5 text-left align-top',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-honey-700',
                    !inMonth && 'bg-paper-50',
                    answer?.source === 'override' &&
                      (answer.isAvailable ? 'bg-honey-50' : 'bg-red-50'),
                  )}
                >
                  <span
                    className={cn(
                      'text-meta',
                      day === today ? 'font-bold text-bark-700' : 'text-ink-700',
                      !inMonth && 'text-ink-500',
                    )}
                  >
                    {Number(day.slice(8, 10))}
                  </span>

                  {override ? (
                    <span className="mt-0.5 block truncate text-[11px] leading-tight text-ink-900">
                      {override.isAvailable
                        ? `${formatWallTime(override.startTime ?? '')}–${formatWallTime(override.endTime ?? '')}`
                        : (override.reason ?? 'Off')}
                    </span>
                  ) : answer?.isAvailable === true ? (
                    <span className="mt-0.5 block text-[11px] leading-tight text-ink-500">
                      {formatDuration(answer.workingMinutes)}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <section className="mt-5">
        <h3 className="text-label text-ink-900">Overrides this month</h3>

        {(overrides.data?.data ?? []).length === 0 ? (
          <div className="mt-2 rounded-lg border border-ink-300 bg-white">
            <EmptyState
              icon={CalendarOff}
              title="No overrides this month."
              hint={`${employeeName} follows their weekly schedule. Pick a day above to book time off, or to add hours on a day they are normally off.`}
            />
          </div>
        ) : (
          <ul className="mt-2 divide-y divide-ink-100 rounded-lg border border-ink-300 bg-white">
            {(overrides.data?.data ?? []).map((entry) => (
              <li key={entry.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                <span className="w-32 shrink-0 text-body text-ink-900">
                  {formatDay(entry.date)}
                </span>
                <span className="min-w-0 flex-1 text-meta text-ink-700">
                  {entry.isAvailable
                    ? `Working ${formatWallTime(entry.startTime ?? '')} – ${formatWallTime(entry.endTime ?? '')} (${formatDuration(entry.workingMinutes)})`
                    : 'Unavailable'}
                  {entry.reason === null ? null : (
                    <span className="text-ink-500"> · {entry.reason}</span>
                  )}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    openFor(entry.date);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove the override on ${formatDay(entry.date)}`}
                  onClick={() => {
                    setRemoving(entry);
                  }}
                >
                  <Trash2 aria-hidden="true" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {draft === null ? null : (
        <OverrideEditor
          draft={draft}
          employeeName={employeeName}
          resolved={byDate.get(draft.date)}
          isExisting={overrideByDate.has(draft.date)}
          isPending={save.isPending}
          error={formError}
          onChange={setDraft}
          onCancel={() => {
            setDraft(null);
            setFormError(null);
          }}
          onSave={onSave}
        />
      )}

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(next) => {
          if (!next) setRemoving(null);
        }}
        title="Remove this override?"
        description={
          <>
            {formatDay(removing?.date ?? '')} goes back to {employeeName}&rsquo;s weekly schedule.
            Jobs already booked that day are not touched.
          </>
        }
        confirmLabel="Remove the override"
        destructive
        isPending={remove.isPending}
        onConfirm={() => {
          const target = removing;
          if (!target) return;
          remove.mutate(target.id, {
            onSuccess: () => {
              toast.success(`${formatDay(target.date)} is back on the weekly schedule.`);
              setRemoving(null);
            },
            onError: (error) => {
              toast.error(
                error instanceof ApiError ? error.message : 'That override could not be removed.',
              );
              setRemoving(null);
            },
          });
        }}
      />
    </div>
  );
}

function Legend() {
  return (
    <ul className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
      <li className="flex items-center gap-1.5 text-meta text-ink-700">
        <span aria-hidden="true" className="size-3 rounded-sm border border-ink-300 bg-red-50" />
        Override — unavailable
      </li>
      <li className="flex items-center gap-1.5 text-meta text-ink-700">
        <span aria-hidden="true" className="size-3 rounded-sm border border-ink-300 bg-honey-50" />
        Override — working
      </li>
      <li className="flex items-center gap-1.5 text-meta text-ink-700">
        <span aria-hidden="true" className="size-3 rounded-sm border border-ink-300 bg-white" />
        Follows the weekly schedule
      </li>
    </ul>
  );
}

/**
 * The editor for one date. Inline rather than a modal, so the calendar it was
 * opened from stays on screen — the answer to "which day was that again" should
 * not need the dialog closing.
 */
function OverrideEditor({
  draft,
  employeeName,
  resolved,
  isExisting,
  isPending,
  error,
  onChange,
  onCancel,
  onSave,
}: {
  draft: DraftOverride;
  employeeName: string;
  /** What the day resolves to today, so the editor can say what is being replaced. */
  resolved: DayAvailability | undefined;
  isExisting: boolean;
  isPending: boolean;
  error: string | null;
  onChange: (draft: DraftOverride) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const invalid = draft.isAvailable && draft.endTime <= draft.startTime;

  return (
    <section className="mt-5 rounded-lg border-2 border-honey-400 bg-white p-4">
      <h3 className="flex items-center gap-1.5 text-h3 text-ink-900">
        <CalendarPlus aria-hidden="true" className="size-4 text-bark-700" />
        {formatDay(draft.date)}
      </h3>

      {/*
        What this replaces, in the resolver's own words. Without it the office is
        overriding something they cannot see, which is how a day gets marked off
        twice and a covering shift gets wiped by accident.
      */}
      {resolved ? (
        <p className="mt-1 text-meta text-ink-500">
          {isExisting ? 'Currently overridden. ' : 'Currently: '}
          {resolved.explanation}
        </p>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3">
        <fieldset className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <legend className="sr-only">Working or not on this day</legend>
          <label className="flex min-h-touch cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="override-availability"
              checked={!draft.isAvailable}
              onChange={() => {
                onChange({ ...draft, isAvailable: false });
              }}
              className="size-4 accent-honey-500"
            />
            <span className="text-body text-ink-900">Unavailable</span>
          </label>
          <label className="flex min-h-touch cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="override-availability"
              checked={draft.isAvailable}
              onChange={() => {
                onChange({ ...draft, isAvailable: true });
              }}
              className="size-4 accent-honey-500"
            />
            <span className="text-body text-ink-900">Working, different hours</span>
          </label>
        </fieldset>

        {draft.isAvailable ? (
          <>
            <div className="flex items-center gap-2">
              <label htmlFor="override-from" className="text-meta text-ink-500">
                From
              </label>
              <input
                id="override-from"
                name="override-from"
                type="time"
                value={draft.startTime}
                onChange={(event) => {
                  onChange({ ...draft, startTime: event.target.value });
                }}
                className="min-h-touch rounded-sm border border-ink-300 bg-white px-2 text-body"
              />
              <label htmlFor="override-to" className="text-meta text-ink-500">
                to
              </label>
              <input
                id="override-to"
                name="override-to"
                type="time"
                value={draft.endTime}
                aria-invalid={invalid || undefined}
                onChange={(event) => {
                  onChange({ ...draft, endTime: event.target.value });
                }}
                className={cn(
                  'min-h-touch rounded-sm border bg-white px-2 text-body',
                  invalid ? 'border-status-priority-text' : 'border-ink-300',
                )}
              />
            </div>

            <div className="flex items-center gap-2">
              <label htmlFor="override-break" className="text-meta text-ink-500">
                Break
              </label>
              <input
                id="override-break"
                name="override-break"
                type="number"
                min={0}
                max={720}
                step={5}
                value={draft.breakMinutes}
                onChange={(event) => {
                  onChange({ ...draft, breakMinutes: Number(event.target.value) });
                }}
                className="min-h-touch w-20 rounded-sm border border-ink-300 bg-white px-2 text-body"
              />
              <span className="text-meta text-ink-500">min</span>
            </div>
          </>
        ) : null}
      </div>

      <div className="mt-4 space-y-1.5">
        <label htmlFor="override-reason" className="block text-label text-ink-900">
          Reason
        </label>
        <input
          id="override-reason"
          name="override-reason"
          type="text"
          maxLength={255}
          value={draft.reason}
          placeholder="Vacation, doctor, covering for Sam…"
          onChange={(event) => {
            onChange({ ...draft, reason: event.target.value });
          }}
          className="min-h-control w-full rounded-sm border border-ink-300 bg-white px-3 text-body placeholder:text-ink-500"
        />
        {/*
          Optional, but it is what the assignment screen shows when it says
          somebody is unavailable — "unavailable" with no reason leaves whoever
          is filling the job with nowhere to go.
        */}
        <p className="text-meta text-ink-500">
          Shown to the office when they try to put {employeeName} on a job that day.
        </p>
      </div>

      {error ? (
        <div className="mt-4">
          <FormError>{error}</FormError>
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button type="button" onClick={onSave} disabled={isPending || invalid}>
          {isPending ? 'Saving…' : isExisting ? 'Update this day' : 'Save this day'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </section>
  );
}
